import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from './supabase';
import { recordWeighIn, newRequestId, SIGNUP_WEIGHT_NOTE } from './services/weightService';
import { recordHealthSharing, HEALTH_SHARING_TEXT } from './services/healthSharingService';
import { Check, ArrowLeft, ArrowRight, Mail, User as UserIcon, Eye, EyeOff } from 'lucide-react';
import { email as validateEmail, passwordStrength, getPasswordRequirements, isPasswordValid, friendlyAuthError } from './utils/validation';
import { toastSuccess } from './ui/toast';
import cipherLogo from './assets/cipher-icon-only-transparent.png';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import CoachCodeInput from './components/CoachCodeInput';
import DateWheelPicker from './components/ui/DateWheelPicker';
import WheelColumn from './components/ui/WheelColumn';
import WeightRuler from './components/ui/WeightRuler';
import { colors as theme } from './theme';

// Last-resort coach assignment. A client row cannot go active without a
// coach_id (DB trigger trigger_validate_client_coach). Normally we resolve the
// super_admin via the get_super_admin_id() RPC; this hardcoded id (SA-AMIR) is
// only used if that RPC call fails, so a transient error can't fail onboarding.
const SUPER_ADMIN_AUTH_ID = '8c173701-5e88-4c75-8d83-e2fae8786e36';

// ── Onboarding draft persistence ─────────────────────────────────────────────
// Answers are saved to localStorage as the user moves through the flow, so a
// refresh, a dropped connection, or an accidental tab close doesn't drop them
// back onto an empty questionnaire. Passwords and photo File objects are never
// written to storage. The draft is cleared once onboarding completes, ignored
// after a week, and only trusted when its `owner` matches the current session.
const DRAFT_KEY = 'atone_onboarding_draft_v1';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const loadOnboardingDraft = (owner) => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || draft.owner !== owner) return null;
    if (!draft.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) return null;
    return draft;
  } catch {
    return null;
  }
};

const saveOnboardingDraft = (owner, currentStep, formData) => {
  try {
    // eslint-disable-next-line no-unused-vars
    const { password, ...safe } = formData;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ owner, currentStep, formData: safe, savedAt: Date.now() }));
  } catch {
    // storage full / disabled — non-fatal, they just don't get resume
  }
};

const clearOnboardingDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
};

// Design System
const colors = {
  primary: theme.gold,
  primaryDark: theme.goldPressed,
  secondary: '#004E89',
  success: theme.success,
  danger: theme.danger,
  bg: theme.bg,
  bgCard: theme.surface1,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
};

const typography = {
  fontDisplay: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
  h2: { size: '22px', weight: 600 },
  body: { size: '15px', weight: 400 },
  small: { size: '13px', weight: 400 },
};

// Shared form field components — hoisted to module scope rather than defined
// inside OnboardingFlow's render. Defining a component inside another
// component's function body creates a brand-new function reference every
// render, so React treats it as a different component type each time and
// unmounts/remounts the real DOM node — losing focus and cursor position on
// every keystroke-triggered re-render. That was causing the cursor to jump
// to the end whenever editing a character mid-string in any of these fields.
const StepContainer = ({ title, subtitle, children, icon }) => (
  <div>
    {icon && (
      <div style={{
        marginBottom: '16px',
        padding: '12px',
        background: `${colors.primary}15`,
        borderRadius: '14px',
        width: 'fit-content',
      }}>
        {icon}
      </div>
    )}
    <h2 style={{
      fontSize: typography.h2.size,
      fontWeight: typography.h2.weight,
      color: colors.text,
      marginBottom: subtitle ? '8px' : '24px',
      fontFamily: typography.fontDisplay,
    }}>
      {title}
    </h2>
    {subtitle && (
      <p style={{
        fontSize: typography.body.size,
        color: colors.textMuted,
        marginBottom: '24px',
      }}>
        {subtitle}
      </p>
    )}
    {children}
  </div>
);

const Input = ({ label, type = 'text', value, onChange, placeholder, error, autoComplete, autoCorrect, autoCapitalize, spellCheck, style, autoFocus }) => {
  const [localValue, setLocalValue] = React.useState(value || '');
  const onChangeRef = React.useRef(onChange);
  const lastSentValueRef = React.useRef(value);
  const isTypingRef = React.useRef(false);

  // Keep ref updated
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync with external value changes ONLY if it's not from our own update
  React.useEffect(() => {
    // Only sync if the value changed externally (not from our debounced update)
    if (value !== localValue && value !== lastSentValueRef.current && !isTypingRef.current) {
      setLocalValue(value || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Passed up at once. It used to wait 150 ms, so Continue straight after typing or autofill could
  // check the old value and seem not to work (14 October 2026).
  const handleChange = (e) => {
    const next = e.target.value;
    setLocalValue(next);
    lastSentValueRef.current = next;
    if (onChangeRef.current) onChangeRef.current(next);
  };

  return (
    <div>
      {label && (
        <label style={{
          display: 'block',
          fontSize: typography.small.size,
          fontWeight: 600,
          color: colors.text,
          marginBottom: '8px',
        }}>
          {label}
        </label>
      )}
      <input
        type={type}
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoCorrect={autoCorrect}
        autoCapitalize={autoCapitalize}
        spellCheck={spellCheck}
        autoFocus={autoFocus}
        style={{
          width: '100%',
          padding: '14px 16px',
          background: colors.bg,
          border: `2px solid ${error ? colors.danger : colors.border}`,
          borderRadius: '8px',
          color: colors.text,
          fontSize: typography.body.size,
          boxSizing: 'border-box',
          outline: 'none',
          transition: 'border-color 0.2s',
          ...style,
        }}
      />
      {error && (
        <p style={{
        fontSize: typography.small.size,
        color: colors.danger,
        marginTop: '6px',
      }}>
        {error}
      </p>
      )}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const TextArea = ({ label, value, onChange, placeholder, error, autoFocus }) => {
  const [localValue, setLocalValue] = React.useState(value || '');
  const onChangeRef = React.useRef(onChange);
  const lastSentValueRef = React.useRef(value);
  const isTypingRef = React.useRef(false);

  // Keep ref updated
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync with external value changes ONLY if it's not from our own update
  React.useEffect(() => {
    // Only sync if the value changed externally (not from our debounced update)
    if (value !== localValue && value !== lastSentValueRef.current && !isTypingRef.current) {
      setLocalValue(value || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced update to parent
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onChangeRef.current && localValue !== value) {
        onChangeRef.current(localValue);
        lastSentValueRef.current = localValue;
      }
      isTypingRef.current = false;
    }, 150); // 150ms debounce

    return () => clearTimeout(timeoutId);
  }, [localValue, value]);

  const handleChange = (e) => {
    isTypingRef.current = true;
    setLocalValue(e.target.value);
  };

  return (
    <div style={{ marginTop: '16px' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: typography.small.size,
          fontWeight: 600,
          color: colors.text,
          marginBottom: '8px',
        }}>
          {label}
        </label>
      )}
      <textarea
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        rows={4}
        autoFocus={autoFocus}
        style={{
          width: '100%',
          padding: '14px 16px',
          background: colors.bg,
          border: `2px solid ${error ? colors.danger : colors.border}`,
          borderRadius: '8px',
          color: colors.text,
          fontSize: typography.body.size,
          boxSizing: 'border-box',
          outline: 'none',
          fontFamily: typography.fontBody,
          resize: 'vertical',
        }}
      />
      {error && (
        <p style={{
          fontSize: typography.small.size,
          color: colors.danger,
          marginTop: '6px',
        }}>
        {error}
      </p>
      )}
    </div>
  );
};

const RadioGroup = ({ options, value, onChange, error }) => (
  <div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          style={{
            padding: '16px',
            background: value === option.value ? `${colors.primary}20` : colors.bg,
            border: `2px solid ${value === option.value ? colors.primary : colors.border}`,
            borderRadius: '14px',
            color: colors.text,
            fontSize: typography.body.size,
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div>{option.label}</div>
            {option.subtitle && (
              <div style={{
                fontSize: typography.small.size,
                color: colors.textMuted,
                fontWeight: 400,
                marginTop: '4px',
              }}>
                {option.subtitle}
              </div>
            )}
          </div>
          {value === option.value && (
            <Check size={20} color={colors.primary} />
          )}
        </button>
      ))}
    </div>
    {error && (
      <p style={{
        fontSize: typography.small.size,
        color: colors.danger,
        marginTop: '6px',
      }}>
        {error}
      </p>
    )}
  </div>
);

const CheckboxGroup = ({ label, options, value, onChange }) => (
  <div style={{ marginTop: '20px' }}>
    {label && (
      <label style={{
        display: 'block',
        fontSize: typography.small.size,
        fontWeight: 600,
        color: colors.text,
        marginBottom: '12px',
      }}>
        {label}
      </label>
    )}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => {
            const newValue = value.includes(option.value)
              ? value.filter(v => v !== option.value)
              : [...value, option.value];
            onChange(newValue);
          }}
          style={{
            padding: '12px 16px',
            background: value.includes(option.value) ? `${colors.primary}20` : colors.bg,
            border: `2px solid ${value.includes(option.value) ? colors.primary : colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            fontSize: typography.body.size,
            fontWeight: 500,
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {option.label}
          {value.includes(option.value) && (
            <Check size={18} color={colors.primary} />
          )}
        </button>
      ))}
    </div>
  </div>
);

// existingAuthUser: set when the caller already has an authenticated session
// (e.g. a first-time Google/Apple sign-in with no `users` row yet) — skips
// the email/password account-creation steps entirely and reuses that
// session's identity in handleComplete instead of calling supabase.auth.signUp().
// Fresh email signup (existingAuthUser undefined) is completely untouched —
// still starts at step 0 and creates the account itself, exactly as before.
const OnboardingFlow = ({ onComplete, existingAuthUser, onGoToLogin }) => {
  // The account gets created at the password step now (not at the very end), so
  // `createdAuthUser` fills the same role `existingAuthUser` does for Google
  // sign-ups: once it's set, the account exists and steps 0-2 are behind us.
  const [createdAuthUser, setCreatedAuthUser] = useState(null);
  const weighInRequestId = useRef(newRequestId()); // the starting weigh-in, once
  const authUser = existingAuthUser || createdAuthUser;

  // Persistent banner shown above the buttons when the final "Complete Signup"
  // write fails — stays put until the next attempt (a 5s toast is too easy to
  // miss on mobile, and the common cause here is a dropped connection).
  const [submitError, setSubmitError] = useState(null);

  const totalSteps = 15;
  // The steps shown, in order (14 October 2026: email and password on one screen, no confirm-password
  // step, no progress photos, which are asked for at the first check-in). Steps keep their numbers so
  // the validation and screens below are unchanged; 1, 2 and 13 are no longer used.
  const STEP_ORDER = existingAuthUser ? [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14] : [0, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14];
  // "Step k of 12" counts the account screen too, so the numbers do not change after a reload
  const NUMBERED_STEPS = [0, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14];
  const LAST_STEP = totalSteps - 1;
  const FIRST_STEP = STEP_ORDER[0];
  // A draft saved before 14 October may stop on a step that no longer exists.
  const shownStep = (step) => (step === 1 || step === 2 ? 0 : step === 13 ? 14 : step);
  const [currentStep, setCurrentStep] = useState(FIRST_STEP);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // null | 'terms' | 'privacy' — shown in-place so onboarding state is never lost
  const [validatedCoach, setValidatedCoach] = useState(null); // set by CoachCodeInput when a valid code is entered

  // Refs for problematic fields
  const injuryDetailsRef = React.useRef(null);
  const passwordRef = React.useRef(null);

  const [formData, setFormData] = useState(() => {
    const base = {
      // Authentication fields
      email: existingAuthUser?.email || '',
      password: '',

      // Existing fields
      name: existingAuthUser?.user_metadata?.full_name || existingAuthUser?.email?.split('@')[0] || '',
      date_of_birth: '',
      sex: '',
      height: '',
      weight: '',
      equipment: '',
      homeEquipment: [],
      exerciseFrequency: '',
      experienceLevel: '',
      hasInjuries: '',
      injuryDetails: '',
      primaryGoal: '',
      wasReferred: '', // '' | 'no' | 'yes' — was the client referred by a coach

      // Legal acceptance; the consent to share health information is its own tick (migration 083)
      agreedToTerms: false,
      agreedToHealthSharing: false,
    };

    // Restore saved answers (never passwords, photos, or consent) if we have a
    // draft for this session's owner.
    const draft = loadOnboardingDraft(existingAuthUser?.id || 'anon');
    if (!draft?.formData) return base;
    return {
      ...base,
      ...draft.formData,
      email: existingAuthUser?.email || draft.formData.email || '',
      password: '',
      agreedToTerms: false,
      agreedToHealthSharing: false,
    };
  });

  // On mount, move forward to where they left off — but for a fresh (non-Google)
  // signup only jump past the account steps if a session actually exists, so we
  // never skip account creation. Defined BEFORE the save effect so it runs first.
  React.useEffect(() => {
    const draft = loadOnboardingDraft(existingAuthUser?.id || 'anon');
    if (draft?.currentStep == null) return;
    const clamp = (s) => shownStep(Math.max(0, Math.min(s, totalSteps - 1)));
    if (existingAuthUser) {
      setCurrentStep(clamp(Math.max(draft.currentStep, 3)));
      return;
    }
    if (draft.currentStep > 2) {
      supabase.auth.getUser()
        .then(({ data }) => {
          if (data?.user) {
            setCreatedAuthUser(data.user);
            setCurrentStep(clamp(draft.currentStep));
          } else {
            setCurrentStep(0); // no live session — answers kept, redo the account step
          }
        })
        .catch(() => setCurrentStep(0));
    } else {
      setCurrentStep(clamp(draft.currentStep));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist progress on every change. Passwords are stripped inside
  // saveOnboardingDraft. Owner switches to the real user id once the account
  // exists, so a later resume (they come back signed-in) still finds the draft.
  //
  // Skip the very first commit: on mount `currentStep` is still the initial
  // useState value (3 or 0), and writing that would clobber the stored step
  // before the resume effect above has moved us there. The first real save
  // happens on the next change.
  const didFirstSaveRef = React.useRef(false);
  React.useEffect(() => {
    if (!didFirstSaveRef.current) {
      didFirstSaveRef.current = true;
      return;
    }
    saveOnboardingDraft(existingAuthUser?.id || createdAuthUser?.id || 'anon', currentStep, formData);
  }, [formData, currentStep, createdAuthUser, existingAuthUser]);

  // Pre-fill from Nutrition profile if this user already has one
  React.useEffect(() => {
    const prefill = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: nutritionProfile } = await supabase
        .from('profiles')
        .select('first_name, date_of_birth, sex, height_cm, current_weight_kg')
        .eq('id', user.id)
        .maybeSingle();
      if (!nutritionProfile) return;
      // Only fill blanks — never clobber an answer the user already gave or one
      // restored from a saved draft.
      setFormData(prev => {
        const u = { ...prev };
        if (nutritionProfile.first_name && !u.name) u.name = nutritionProfile.first_name;
        if (nutritionProfile.sex && ['male', 'female'].includes(nutritionProfile.sex) && !u.sex) u.sex = nutritionProfile.sex;
        if (nutritionProfile.height_cm && !u.height) u.height = String(nutritionProfile.height_cm);
        if (nutritionProfile.current_weight_kg && !u.weight) u.weight = String(nutritionProfile.current_weight_kg);
        if (nutritionProfile.date_of_birth && !u.date_of_birth) {
          const [yyyy, mm, dd] = nutritionProfile.date_of_birth.split('-');
          u.date_of_birth = `${dd}/${mm}/${yyyy}`;
        }
        return u;
      });
    };
    prefill();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const calcAgeFromDOB = (dd, mm, yyyy) => {
    const today = new Date();
    let age = today.getFullYear() - parseInt(yyyy);
    if (today < new Date(today.getFullYear(), parseInt(mm) - 1, parseInt(dd))) age--;
    return age;
  };

  // Wheel picker <-> "DD/MM/YYYY" string conversion. formData.date_of_birth stays
  // that same string throughout (validation + submission are untouched) — the
  // wheel just changes how that string gets produced.
  const pad2 = (n) => String(n).padStart(2, '0');
  const dobToWheelValue = (dobString) => {
    const match = (dobString || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      return { day: parseInt(match[1], 10), month: parseInt(match[2], 10), year: parseInt(match[3], 10) };
    }
    const defaultYear = new Date().getFullYear() - 25; // sensible starting point, not an arbitrary edge
    return { day: 1, month: 1, year: defaultYear };
  };
  const wheelValueToDob = ({ day, month, year }) => `${pad2(day)}/${pad2(month)}/${year}`;

  const sections = [
    { name: 'Account', steps: [0] },
    { name: 'About You', steps: [3, 4, 5, 6, 7] },
    { name: 'Training', steps: [8, 9, 10] },
    { name: 'Health', steps: [11] },
    { name: 'Goals', steps: [12, 14] },
  ];

  // The wheels show a starting date, height and weight (1 January, 175 cm, 75 kg) and report it as
  // the answer so Continue works. Until 14 October 2026 that starting value was kept without a word,
  // so a coach could read made-up numbers as the client's. Now a value the client never moved is
  // asked about once: "Is this right?". A second Continue keeps it.
  const [untouched, setUntouched] = useState({});
  const [askedToConfirm, setAskedToConfirm] = useState(null);
  const measurementChange = (field, value, alreadySet) => {
    // The picker's first report, while the field is still empty, is its starting value
    setUntouched(prev => ({ ...prev, [field]: !alreadySet }));
    updateFormData(field, value);
  };

  const updateFormData = React.useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationErrors(prev => {
      const hasFieldErr = prev[field];
      const hasEmailTaken = field === 'email' && prev._emailTaken;
      if (!hasFieldErr && !hasEmailTaken) return prev;
      const newErrors = { ...prev };
      delete newErrors[field];
      if (hasEmailTaken) delete newErrors._emailTaken;
      return newErrors;
    });
  }, []);

  // Handle password input with cursor preservation
  const handlePasswordChange = (e, field, ref) => {
    const cursorPosition = e.target.selectionStart;
    const value = e.target.value;

    updateFormData(field, value);

    // Restore cursor position after React updates
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  };

  const validateCurrentStep = () => {
    const errors = {};

    switch (currentStep) {
      // Step 0 - Email and password
      case 0: {
        const emailError = validateEmail(formData.email);
        if (emailError) {
          errors.email = emailError;
        }
        const passwordError = passwordStrength(formData.password);
        if (passwordError) {
          errors.password = passwordError;
        }
        break;
      }
        
      // Existing validations (shifted by +3)
      case 3: // Name
        if (!formData.name || formData.name.trim().length < 2) {
          errors.name = "Name required (min 2 chars)";
        }
        break;
      case 4: { // Date of birth
        const dobMatch = formData.date_of_birth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!dobMatch) {
          errors.date_of_birth = 'Enter your date of birth as DD/MM/YYYY';
          break;
        }
        const [, dd, mm, yyyy] = dobMatch;
        const date = new Date(`${yyyy}-${mm}-${dd}`);
        if (isNaN(date.getTime()) || date.getMonth() + 1 !== parseInt(mm)) {
          errors.date_of_birth = 'Please enter a valid date';
          break;
        }
        const dobAge = calcAgeFromDOB(dd, mm, yyyy);
        if (dobAge < 13 || dobAge > 100) {
          errors.date_of_birth = 'Age must be between 13 and 100';
        }
        break;
      }
      case 5: // Sex
        if (!formData.sex) errors.sex = "Please select";
        break;
      case 6: // Height
        const height = parseInt(formData.height);
        if (!formData.height || isNaN(height) || height < 100 || height > 250) {
          errors.height = "Height: 100-250cm";
        }
        break;
      case 7: // Weight
        const weight = parseFloat(formData.weight);
        if (!formData.weight || isNaN(weight) || weight < 30 || weight > 300) {
          errors.weight = "Weight: 30-300kg";
        }
        break;
      case 8: // Equipment
        if (!formData.equipment) errors.equipment = "Please select";
        break;
      case 9: // Exercise Frequency
        if (!formData.exerciseFrequency) errors.exerciseFrequency = "Please select";
        break;
      case 10: // Experience Level
        if (!formData.experienceLevel) errors.experienceLevel = "Please select";
        break;
      case 11: // Injuries
        if (!formData.hasInjuries) {
          errors.hasInjuries = "Please answer";
        }
        if (formData.hasInjuries === 'yes') {
          // Check ref value, not state value (since it's uncontrolled)
          const injuryText = injuryDetailsRef.current?.value || '';
          if (injuryText.trim().length < 5) {
            errors.injuryDetails = "Please explain (min 5 chars)";
          }
        }
        break;
      case 12: // Primary Goal
        if (!formData.primaryGoal) errors.primaryGoal = "Please select";
        break;
      case 14: // Coach referral + legal acceptance
        if (!formData.wasReferred) {
          errors.wasReferred = 'Please answer';
        } else if (formData.wasReferred === 'yes' && !validatedCoach?.auth_user_id) {
          errors.wasReferred = 'Enter your coach’s code above, or choose "No".';
        }
        if (!formData.agreedToTerms) {
          errors.agreedToTerms = 'You must accept the Terms of Service and Privacy Policy to continue.';
        }
        // Owner, 18 October 2026: coaching needs it, so it is asked for here, as its own tick
        if (!formData.agreedToHealthSharing) {
          errors.agreedToHealthSharing = 'Please agree to share your health information with your coach, so they can coach you.';
        }
        break;
      default:
        break;
    }

    const measured = { 4: 'date_of_birth', 6: 'height', 7: 'weight' }[currentStep];
    if (measured && !errors[measured] && untouched[measured] && askedToConfirm !== currentStep) {
      errors[measured] = 'Is this right? Scroll to change it, or tap Continue again to keep it.';
      setAskedToConfirm(currentStep);
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = async () => {
    if (submitError) setSubmitError(null);
    // Capture injury details from ref before moving forward (step 11)
    if (currentStep === 11 && formData.hasInjuries === 'yes') {
      if (injuryDetailsRef.current) {
        setFormData(prev => ({ ...prev, injuryDetails: injuryDetailsRef.current.value }));
      }
    }

    if (!validateCurrentStep()) {
      return;
    }

    // (No step-0 "email already registered" pre-check: RLS blocks an anonymous
    // SELECT on `users`, so it always returned nothing. A duplicate email is
    // caught authoritatively at the step-2 signUp below, which bounces back
    // here with the "Log in instead" link.)

    // Create the Supabase account the moment email + password are
    // done — not at the end. A weak/breached password, an email that's already
    // taken, or a dropped connection now surfaces HERE, on the password screen,
    // and they retry in place instead of losing the whole questionnaire.
    // Skipped for Google sign-ups (existingAuthUser) and once already created.
    if (currentStep === 0 && !authUser) {
      setIsSubmitting(true);
      try {
        const cleanEmail = formData.email.trim().toLowerCase();
        const { data, error } = await supabase.auth.signUp({
          email: cleanEmail,
          password: formData.password,
        });

        if (error || !data?.user) {
          const { field, message, code } = friendlyAuthError(error || {});
          setValidationErrors({
            [field]: message,
            ...(code === 'already_registered' ? { _emailTaken: true } : {}),
          });
          setCurrentStep(0);
          return;
        }

        if (!data.session) {
          // Project auto-confirms email, so this shouldn't happen — but if it
          // ever does, don't march them through a flow they can't finish.
          setValidationErrors({ email: 'Check your inbox to confirm your email, then log in to continue.' });
          setCurrentStep(0);
          return;
        }

        setCreatedAuthUser(data.user);
        setFormData(prev => ({ ...prev, email: cleanEmail }));
        setCurrentStep(3);
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    const at = STEP_ORDER.indexOf(currentStep);
    if (currentStep !== LAST_STEP && at >= 0 && at < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[at + 1]);
    } else {
      handleComplete();
    }
  };

  // Once the account exists, the email/password screen is done for good — the
  // floor rises to step 3 so "Back" can't return to it, and Back is not shown there.
  const backFloor = authUser ? 3 : FIRST_STEP;
  const backAt = STEP_ORDER.indexOf(currentStep);
  const canGoBack = backAt > 0 && STEP_ORDER[backAt - 1] >= backFloor;
  const prevStep = () => {
    if (canGoBack) setCurrentStep(STEP_ORDER[backAt - 1]);
  };

  // Someone who already has a profile: nothing of theirs is changed, and they go to their own screens
  const leaveAlreadySignedUp = () => {
    clearOnboardingDraft();
    toastSuccess("You're already signed up, so nothing was changed. To make a new account, sign out first.");
    if (onComplete) onComplete(null);
  };

  const handleComplete = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Capture injury details from ref in case it wasn't synced to state
      const injuryValue = injuryDetailsRef.current?.value || formData.injuryDetails;

      const dobMatch = formData.date_of_birth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      const dobISO = dobMatch ? `${dobMatch[3]}-${dobMatch[2]}-${dobMatch[1]}` : null;
      const calculatedAge = dobMatch ? calcAgeFromDOB(dobMatch[1], dobMatch[2], dobMatch[3]) : null;

      // Step 1: Resolve the Supabase Auth account. Normally it already exists by
      // now — created at the password step (createdAuthUser) or via Google
      // (existingAuthUser). The signUp fallback only runs if neither is set
      // (e.g. an unusually old resumed draft), so a missing account can't dead-
      // end the flow here.
      let cleanEmail, authUserId;
      if (authUser) {
        // An account that has already finished sign-up is never written over: reaching sign-up while
        // signed in (the phone's Back, then "Sign up") replaced the client's answers and coach (owner,
        // 20 October 2026). Nothing is saved; they go to their own screens.
        const { data: alreadySignedUp, error: lookupError } = await supabase
          .from('users')
          .select('id')
          .eq('auth_user_id', authUser.id)
          .maybeSingle();
        if (lookupError) throw lookupError;
        if (alreadySignedUp) {
          leaveAlreadySignedUp();
          return;
        }
        cleanEmail = authUser.email;
        authUserId = authUser.id;
      } else {
        cleanEmail = formData.email.trim().toLowerCase();

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: cleanEmail,
          password: formData.password,
        });

        if (authError) throw authError;

        if (!authData.user) {
          throw new Error('Account creation failed');
        }
        authUserId = authData.user.id;
      }


      // Step 2: Create user profile in users table
      const dataToInsert = {
        email: cleanEmail,
        auth_user_id: authUserId,
        name: formData.name,
        role: 'client',
        status: 'active',
        approved_at: new Date().toISOString(),
        coach_code: null,  // ✅ Clients don't have coach codes (only coaches do)
        age: calculatedAge,
        date_of_birth: dobISO,
        sex: formData.sex,
        height: parseInt(formData.height),
        weight: parseFloat(formData.weight),
        bodyweight_kg: parseFloat(formData.weight),
        bodyweight_updated_at: new Date().toISOString(),
        training_location: formData.equipment,
        exercise_frequency: formData.exerciseFrequency,
        experience_level: formData.experienceLevel,
        primary_goal: formData.primaryGoal,
        has_injuries: formData.hasInjuries === 'yes',
        is_active: true,
      };

      // Add optional fields
      if (formData.equipment === 'home' && formData.homeEquipment.length > 0) {
        dataToInsert.home_equipment = formData.homeEquipment;
      }
      if (formData.hasInjuries === 'yes' && injuryValue) {
        dataToInsert.injuries = injuryValue;
      }

      // Assign to the coach they were referred by (answered "Yes" + entered a
      // valid code); otherwise everyone falls under the super_admin umbrella.
      // Resolve via SECURITY DEFINER RPC (new users can't query the users table
      // directly), with a hardcoded fallback so a transient RPC failure can't
      // break onboarding at the final step.
      if (formData.wasReferred === 'yes' && validatedCoach?.auth_user_id) {
        dataToInsert.coach_id = validatedCoach.auth_user_id;
      } else {
        try {
          const { data: superAdminId } = await supabase.rpc('get_super_admin_id');
          dataToInsert.coach_id = superAdminId || SUPER_ADMIN_AUTH_ID;
        } catch (e) {
          console.warn('[onboarding] get_super_admin_id failed, using fallback:', e?.message);
          dataToInsert.coach_id = SUPER_ADMIN_AUTH_ID;
        }
      }


      // Insert, never upsert: the database allows one profile per sign-in (users_auth_user_id_key), so
      // a profile made after the check above (another tab, a second tap) is refused rather than written
      // over, and the weigh-in, consent and programme below are not run for it (Codex's task 019, R5).
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert(dataToInsert)
        .select()
        .single();

      if (userError) {
        if (userError.code === '23505' && /auth_user_id/.test(`${userError.message} ${userError.details}`)) {
          leaveAlreadySignedUp();
          return;
        }
        throw userError;
      }

      // Profile row is in — the questionnaire is done, so drop the saved draft.
      clearOnboardingDraft();

      // Step 2a: the consent to share health information with the coach, recorded by the database
      // with its time (migration 083). Tried twice; if it still fails, the app asks again on first
      // open, so the account is never blocked by it.
      try {
        const saveConsent = () => recordHealthSharing(authUserId, true, 'signup');
        await saveConsent().catch(saveConsent);
      } catch (consentErr) {
        console.warn('[OnboardingFlow] health sharing consent not recorded; asked again in the app:', consentErr);
      }

      // Step 2b: the starting weight is the first weigh-in in the one weight record (migration
      // 080); the database sets the current weight in both apps. One request id per sign-up, so
      // pressing Complete again cannot add it twice. Never blocks the account.
      try {
        await recordWeighIn(authUserId, parseFloat(formData.weight), {
          notes: SIGNUP_WEIGHT_NOTE, requestId: weighInRequestId.current,
        });
      } catch (syncErr) {
        console.warn('[OnboardingFlow] starting weight not added to the weight record (non-fatal):', syncErr);
      }

      // Step 3: Auto-assign best matching program from library (fire and forget)
      // getSession() rather than authData.session directly — the latter only
      // exists on the fresh-signUp path; both paths already have an active
      // client-side session by this point (signUp establishes one immediately
      // since email confirmation is disabled; existingAuthUser already had one).
      const { data: { session: matchSession } } = await supabase.auth.getSession();
      const matchToken = matchSession?.access_token;
      if (matchToken) {
        fetch('/api/match-program', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${matchToken}` },
          body: JSON.stringify({}),
        }).catch(err => console.error('[match-program]', err));
      }

      toastSuccess('Account created successfully! You can now log in.');

      if (onComplete) {
        onComplete(userData);
      }
      
    } catch (error) {

      const rawMsg = error?.message || '';
      // A dropped connection (wifi→cell handoff, tunnel, flaky mobile data, a
      // filtering proxy) surfaces as a fetch TypeError or navigator.onLine=false,
      // not a real server error — call it what it is.
      const isNetworkError =
        !navigator.onLine ||
        error?.name === 'TypeError' ||
        /failed to fetch|networkerror|load failed|fetch failed|network request failed|timeout/i.test(rawMsg);

      let errorMessage = rawMsg || 'Something went wrong. Please try again.';

      if (isNetworkError) {
        errorMessage = 'You’re offline, or the connection dropped. Reconnect and tap "Complete Signup" again — everything you entered is saved.';
      } else if (error.code === '23505' && error.details?.includes('coach_code')) {
        errorMessage = 'There was an issue with the coach code. Please contact support.';
      } else if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        errorMessage = 'This email is already registered. Please use a different email or try logging in.';
      } else if (errorMessage.includes('invalid') && errorMessage.includes('email')) {
        errorMessage = 'Invalid email address. Please check and try again.';
      } else if (errorMessage.toLowerCase().includes('password') && !authUser) {
        // Only meaningful on the rare fallback path where the account is still
        // being created here. With an account already in hand, never bounce
        // them backward — keep them on the final step and let them retry.
        setCurrentStep(0);
        setValidationErrors({ password: errorMessage });
        setIsSubmitting(false);
        return;
      } else if (authUser) {
        errorMessage = 'Couldn’t save your details just now — tap "Complete Signup" to try again. Your answers are kept.';
      }

      // Persistent banner (see submitError render). No toast — it duplicates the
      // banner and vanishes before someone reconnecting can read it.
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      // Step 0 - Email and password, on one screen
      case 0: {
        const requirements = getPasswordRequirements(formData.password);
        return (
          <StepContainer title="Create your account" icon={<Mail size={24} color={colors.primary} />}>
            <p style={{ color: theme.textMuted, fontSize: 14, lineHeight: 1.6 }}>Your account → your training → your plan → your membership</p>
            <Input
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(val) => updateFormData('email', val)}
              placeholder="your.email@example.com"
              error={validationErrors.email}
              autoComplete="email"
              autoFocus
            />
            {validationErrors._emailTaken && onGoToLogin && (
              <button
                type="button"
                onClick={onGoToLogin}
                style={{
                  marginTop: '12px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: colors.primary,
                  fontSize: typography.small.size,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Log in instead <ArrowRight size={16} />
              </button>
            )}
            <p style={{
              fontSize: typography.small.size,
              color: colors.textMuted,
              marginTop: '12px',
              lineHeight: '1.5'
            }}>
              You'll use this to log in and access your program.
            </p>
            <div style={{ height: '20px' }} />
            <div>
              <label style={{
                display: 'block',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.text,
                marginBottom: '8px',
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handlePasswordChange(e, 'password', passwordRef)}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '14px 48px 14px 16px',
                    background: colors.bg,
                    border: `2px solid ${validationErrors.password ? colors.danger : colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: typography.body.size,
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {showPassword ? (
                    <EyeOff size={20} color={colors.textMuted} />
                  ) : (
                    <Eye size={20} color={colors.textMuted} />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <p style={{
                  fontSize: typography.small.size,
                  color: colors.danger,
                  marginTop: '6px',
                }}>
                  {validationErrors.password}
                </p>
              )}
            </div>
            {/* Password requirements checklist */}
            <div style={{ marginTop: '16px' }}>
              {requirements.map((req, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '6px',
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: req.met ? colors.success : colors.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                  }}>
                    {req.met && <Check size={10} color={theme.text} />}
                  </div>
                  <span style={{
                    fontSize: typography.small.size,
                    color: req.met ? colors.success : colors.textMuted,
                    transition: 'color 0.2s',
                  }}>
                    {req.label}
                  </span>
                </div>
              ))}
            </div>
          </StepContainer>
        );
      }

      // Existing steps (shifted by +3)
      case 3:
        return (
          <StepContainer title="What's your name?" icon={<UserIcon size={24} color={colors.primary} />}>
            <Input
              label="Full Name"
              value={formData.name}
              onChange={(val) => updateFormData('name', val)}
              placeholder="Enter name"
              error={validationErrors.name}
              autoComplete="name"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              autoFocus
            />
          </StepContainer>
        );

      case 4: {
        const currentYear = new Date().getFullYear();
        return (
          <StepContainer title="Date of birth?" subtitle="Used to calculate your training needs">
            <div>
              <label style={{
                display: 'block',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.text,
                marginBottom: '8px',
              }}>
                Date of birth
              </label>
              <DateWheelPicker
                value={dobToWheelValue(formData.date_of_birth)}
                isUnset={!formData.date_of_birth}
                onChange={(v) => measurementChange('date_of_birth', wheelValueToDob(v), !!formData.date_of_birth)}
                minYear={currentYear - 100}
                maxYear={currentYear - 13}
              />
              {validationErrors.date_of_birth && (
                <p style={{ fontSize: typography.small.size, color: colors.danger, marginTop: '6px' }}>
                  {validationErrors.date_of_birth}
                </p>
              )}
            </div>
          </StepContainer>
        );
      }

      case 5:
        return (
          <StepContainer title="Biological sex?">
            <RadioGroup
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' }
              ]}
              value={formData.sex}
              onChange={(val) => updateFormData('sex', val)}
              error={validationErrors.sex}
            />
          </StepContainer>
        );

      case 6: {
        const heightValue = parseInt(formData.height, 10) || 175;
        const heightOptions = Array.from({ length: 151 }, (_, i) => {
          const cm = 100 + i;
          return { value: cm, label: `${cm} cm` };
        });
        return (
          <StepContainer title="Your height?">
            <label style={{
              display: 'block',
              fontSize: typography.small.size,
              fontWeight: 600,
              color: colors.text,
              marginBottom: '8px',
            }}>
              Height (cm)
            </label>
            <WheelColumn
              options={heightOptions}
              value={heightValue}
              isUnset={!formData.height}
              onChange={(v) => measurementChange('height', String(v), !!formData.height)}
            />
            {validationErrors.height && (
              <p style={{ fontSize: typography.small.size, color: colors.danger, marginTop: '6px' }}>
                {validationErrors.height}
              </p>
            )}
          </StepContainer>
        );
      }

      case 7: {
        const weightValue = parseFloat(formData.weight) || 75;
        return (
          <StepContainer title="Current weight?">
            <WeightRuler
              value={weightValue}
              isUnset={!formData.weight}
              onChange={(v) => measurementChange('weight', String(v), !!formData.weight)}
              min={30}
              max={300}
            />
            {validationErrors.weight && (
              <p style={{ fontSize: typography.small.size, color: colors.danger, marginTop: '6px', textAlign: 'center' }}>
                {validationErrors.weight}
              </p>
            )}
          </StepContainer>
        );
      }

      case 8:
        return (
          <StepContainer title="Where will you train?">
            <RadioGroup
              options={[
                { value: 'home', label: 'Home' },
                { value: 'gym', label: 'Gym' }
              ]}
              value={formData.equipment}
              onChange={(val) => updateFormData('equipment', val)}
              error={validationErrors.equipment}
            />
            {formData.equipment === 'home' && (
              <CheckboxGroup
                label="Available equipment"
                options={[
                  { value: 'dumbbells', label: 'Dumbbells' },
                  { value: 'resistance_bands', label: 'Resistance Bands' },
                  { value: 'pull_up_bar', label: 'Pull-up Bar' },
                  { value: 'bench', label: 'Bench' },
                  { value: 'bodyweight_only', label: 'Bodyweight Only' }
                ]}
                value={formData.homeEquipment}
                onChange={(val) => updateFormData('homeEquipment', val)}
              />
            )}
          </StepContainer>
        );

      case 9:
        return (
          <StepContainer title="How often will you train?">
            <RadioGroup
              options={[
                { value: '2-3', label: '2-3 days/week' },
                { value: '3-4', label: '3-4 days/week' },
                { value: '4-5', label: '4-5 days/week' },
                { value: '5-6', label: '5-6 days/week' }
              ]}
              value={formData.exerciseFrequency}
              onChange={(val) => updateFormData('exerciseFrequency', val)}
              error={validationErrors.exerciseFrequency}
            />
          </StepContainer>
        );

      case 10:
        return (
          <StepContainer title="Experience level?">
            <RadioGroup
              options={[
                { value: 'beginner', label: 'Beginner', subtitle: 'New to training' },
                { value: 'intermediate', label: 'Intermediate', subtitle: '6+ months experience' },
                { value: 'advanced', label: 'Advanced', subtitle: '2+ years experience' }
              ]}
              value={formData.experienceLevel}
              onChange={(val) => updateFormData('experienceLevel', val)}
              error={validationErrors.experienceLevel}
            />
          </StepContainer>
        );

      case 11:
        return (
          <StepContainer title="Any injuries or conditions?">
            <RadioGroup
              options={[
                { value: 'no', label: 'No injuries' },
                { value: 'yes', label: 'Yes, I have injuries' }
              ]}
              value={formData.hasInjuries}
              onChange={(val) => {
                updateFormData('hasInjuries', val);
                if (val === 'no') {
                  updateFormData('injuryDetails', '');
                }
              }}
              error={validationErrors.hasInjuries}
            />
            {formData.hasInjuries === 'yes' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: typography.small.size,
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: '8px',
                }}>
                  Please describe
                </label>
                <textarea
                  ref={injuryDetailsRef}
                  defaultValue={formData.injuryDetails}
                  placeholder="e.g., Lower back pain, knee issues..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: colors.bg,
                    border: `2px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: typography.body.size,
                    boxSizing: 'border-box',
                    outline: 'none',
                    fontFamily: typography.fontBody,
                    resize: 'vertical',
                  }}
                />
                {validationErrors.injuryDetails && (
                  <p style={{
                    fontSize: typography.small.size,
                    color: colors.danger,
                    marginTop: '6px',
                  }}>
                    {validationErrors.injuryDetails}
                  </p>
                )}
              </div>
            )}
          </StepContainer>
        );

      case 12:
        return (
          <StepContainer title="Primary goal?">
            <RadioGroup
              options={[
                { value: 'lose_weight', label: 'Lose Weight' },
                { value: 'build_muscle', label: 'Build Muscle' },
                { value: 'body_recomp', label: 'Build Muscle & Stay Same Weight' },
                { value: 'get_stronger', label: 'Get Stronger' },
                { value: 'improve_fitness', label: 'Improve Fitness' },
                { value: 'maintain', label: 'Maintain Current Level' }
              ]}
              value={formData.primaryGoal}
              onChange={(val) => updateFormData('primaryGoal', val)}
              error={validationErrors.primaryGoal}
            />
          </StepContainer>
        );

      case 14:
        return (
          <StepContainer
            title="Almost there!"
            icon={<UserIcon size={24} color={colors.primary} />}
          >
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.text,
                marginBottom: '10px',
              }}>
                Were you referred by a coach?
              </label>
              <RadioGroup
                options={[
                  { value: 'no', label: 'No' },
                  { value: 'yes', label: 'Yes, a coach referred me' },
                ]}
                value={formData.wasReferred}
                onChange={(val) => {
                  updateFormData('wasReferred', val);
                  if (val !== 'yes') setValidatedCoach(null);
                }}
                error={validationErrors.wasReferred}
              />
              <p style={{
                fontSize: typography.small.size,
                color: colors.textMuted,
                marginTop: '10px',
                lineHeight: 1.6,
              }}>
                This isn’t a discount code — it just connects you to your trainer.
                Promo codes for a free month are entered later, on the payment screen.
              </p>
              {formData.wasReferred === 'yes' && (
                <div style={{ marginTop: '16px' }}>
                  <CoachCodeInput onCoachValidated={setValidatedCoach} hideHelp />
                </div>
              )}
            </div>
            <div style={{ marginTop: '8px' }}>
              <button
                onClick={() => updateFormData('agreedToTerms', !formData.agreedToTerms)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  minWidth: '22px',
                  borderRadius: '8px',
                  border: `2px solid ${formData.agreedToTerms ? colors.primary : colors.border}`,
                  background: formData.agreedToTerms ? colors.primary : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '1px',
                  transition: 'all 0.2s',
                }}>
                  {formData.agreedToTerms && <Check size={14} color={theme.text} strokeWidth={3} />}
                </div>
                <span style={{ fontSize: '14px', color: colors.textMuted, lineHeight: 1.5 }}>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setLegalModal('terms'); }}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      font: 'inherit', color: colors.primary, textDecoration: 'underline',
                    }}
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setLegalModal('privacy'); }}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      font: 'inherit', color: colors.primary, textDecoration: 'underline',
                    }}
                  >
                    Privacy Policy
                  </button>
                </span>
              </button>
              {validationErrors.agreedToTerms && (
                <p style={{ fontSize: '13px', color: colors.danger, marginTop: '8px' }}>
                  {validationErrors.agreedToTerms}
                </p>
              )}
            </div>
            {/* Separate from the terms, as the ICO asks of consent to health data (migration 083) */}
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                role="checkbox"
                aria-checked={formData.agreedToHealthSharing}
                onClick={() => updateFormData('agreedToHealthSharing', !formData.agreedToHealthSharing)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px', background: 'none', border: 'none',
                  cursor: 'pointer', padding: 0, textAlign: 'left', width: '100%',
                }}
              >
                <div style={{
                  width: '22px', height: '22px', minWidth: '22px', borderRadius: '8px',
                  border: `2px solid ${formData.agreedToHealthSharing ? colors.primary : colors.border}`,
                  background: formData.agreedToHealthSharing ? colors.primary : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px', transition: 'all 0.2s',
                }}>
                  {formData.agreedToHealthSharing && <Check size={14} color={theme.text} strokeWidth={3} />}
                </div>
                <span style={{ fontSize: '14px', color: colors.textMuted, lineHeight: 1.5 }}>
                  {HEALTH_SHARING_TEXT}
                </span>
              </button>
              {validationErrors.agreedToHealthSharing && (
                <p style={{ fontSize: '13px', color: colors.danger, marginTop: '8px' }}>
                  {validationErrors.agreedToHealthSharing}
                </p>
              )}
            </div>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  // The password steps (1-2) gate "Continue" so a weak or mismatched password
  // can't get past this screen — the on-screen checklist shows why. Every other
  // step keeps its existing validate-on-click behaviour (inline messages), so
  // it stays enabled.
  const stepIsValid = (() => {
    switch (currentStep) {
      case 0:
        return isPasswordValid(formData.password);
      default:
        return true;
    }
  })();
  const nextDisabled = isSubmitting || !stepIsValid;

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: typography.fontBody,
    }}>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        {/* Cipher Branding */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            width: '100px', height: '100px', marginBottom: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%',
            margin: '0 auto 10px',
            background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
          }}>
            <img src={cipherLogo} alt="Cipher" style={{
              width: '92px', height: '92px',
            }} />
          </div>
          <div style={{ color: theme.gold, fontSize: '16px', fontWeight: 700, letterSpacing: '4px', fontFamily: "'Orbitron', sans-serif" }}>
            CIPHER
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '12px',
          }}>
            {sections.map((section, idx) => (
              <div key={idx} style={{
                fontSize: typography.small.size,
                color: section.steps.includes(currentStep) ? colors.primary : colors.textMuted,
                fontWeight: 600,
              }}>
                {section.name}
              </div>
            ))}
          </div>
          <div style={{
            width: '100%',
            height: '6px',
            background: colors.bgCard,
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((NUMBERED_STEPS.indexOf(currentStep) + 1) / NUMBERED_STEPS.length) * 100}%` }}
              transition={{ duration: 0.3 }}
              style={{
                height: '100%',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.primaryDark})`,
              }}
            />
          </div>
          <p style={{
            fontSize: typography.small.size,
            color: colors.textMuted,
            marginTop: '8px',
            textAlign: 'center',
          }}>
            Step {NUMBERED_STEPS.indexOf(currentStep) + 1} of {NUMBERED_STEPS.length}
          </p>
        </div>

        {/* Content. The keyboard's Go / Enter in a one-line box is the same as Continue. */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.keyCode === 13) && e.target.tagName === 'INPUT' && !nextDisabled) {
                e.preventDefault();
                nextStep();
              }
            }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            style={{
              background: colors.bgCard,
              padding: '32px',
              borderRadius: '20px',
              border: `1px solid ${colors.border}`,
              marginBottom: '20px',
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Persistent submit-failure banner — final step only */}
        {submitError && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              background: 'rgba(231, 76, 60, 0.12)',
              border: `1px solid ${colors.danger}`,
              borderRadius: '14px',
              padding: '14px 16px',
              marginBottom: '16px',
            }}
          >
            <span style={{ fontSize: '16px', lineHeight: '20px' }}>{!navigator.onLine ? '📡' : '⚠️'}</span>
            <p style={{ margin: 0, fontSize: typography.small.size, color: colors.text, lineHeight: '1.5' }}>
              {submitError}
            </p>
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {canGoBack && (
            <button
              onClick={prevStep}
              style={{
                padding: '16px 24px',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '14px',
                color: colors.text,
                fontSize: typography.body.size,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <ArrowLeft size={20} />
              Back
            </button>
          )}

          <button
            onClick={nextStep}
            disabled={nextDisabled}
            style={{
              flex: 1,
              padding: '16px 24px',
              background: nextDisabled
                ? colors.border
                : `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              border: 'none',
              borderRadius: '14px',
              // dark text on gold, as on every gold button since the one design (PR #42)
              color: nextDisabled ? theme.textMuted : theme.bg,
              fontSize: typography.body.size,
              fontWeight: 700,
              cursor: nextDisabled ? 'not-allowed' : 'pointer',
              opacity: (!stepIsValid && !isSubmitting) ? 0.55 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'opacity 0.2s',
            }}
          >
            {isSubmitting
              ? (currentStep === 0 ? 'Creating your account…' : 'Creating Account...')
              : currentStep === LAST_STEP
                ? 'Complete Signup'
                : 'Continue'
            }
            {!isSubmitting && <ArrowRight size={20} />}
          </button>
        </div>
      </div>

      {legalModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            overflowY: 'auto',
            background: theme.surface1,
          }}
        >
          {legalModal === 'terms' ? (
            <TermsOfService onBack={() => setLegalModal(null)} />
          ) : (
            <PrivacyPolicy onBack={() => setLegalModal(null)} />
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow;
